/**
 * PersonaReviewIssues — read-only render of the persona-review issues already
 * persisted for a section (``ReviewVerdictDoc``), loaded sidecar-independent via
 * GET /sections/{moduleId}/review-issues. Gated on data presence (renders
 * nothing when no verdict exists), so it shows persisted issues even while the
 * live persona sidecar is disabled (``REVIEW_AGENT_ENABLED``).
 *
 * Extracted from the former ReviewTabPanel when the Review tab was folded into
 * the Issues tab (``DataGapsTabPanel``): the regulatory run affordance moved into
 * the Issues header, and these read-only persona issues render beneath the ledger.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useState } from "react";
import { getReviewIssues, type ReviewIssue } from "../../../api/client";
import { fonts, inkColors } from "../../tokens";

const SEVERITY_COLORS: Record<string, string> = {
	critical: "#B71C1C",
	major: "#C77700",
	minor: "#5A5170",
};

function severityColor(sev: string): string {
	return SEVERITY_COLORS[sev.toLowerCase()] ?? "#1565C0";
}

const useStyles = makeStyles({
	card: {
		border: "1px solid #E3E8EF",
		borderRadius: "6px",
		padding: "10px 12px",
		margin: "12px 2px",
	},
	cardHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "8px",
		marginBottom: "8px",
	},
	cardTitle: {
		fontFamily: fonts.body,
		fontSize: "12px",
		fontWeight: 600,
		color: inkColors.ink2,
	},
	reportList: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" },
	report: {
		borderRadius: "5px",
		padding: "8px 10px",
		background: "#F7FAFF",
		borderLeft: "3px solid #1565C0",
	},
	reportHeader: {
		display: "flex",
		alignItems: "baseline",
		justifyContent: "space-between",
		gap: "8px",
		marginBottom: "4px",
	},
	reportTitle: {
		fontFamily: fonts.body,
		fontSize: "11.5px",
		fontWeight: 600,
		color: "#1565C0",
		lineHeight: 1.35,
	},
	badge: {
		flexShrink: 0,
		fontFamily: fonts.body,
		fontSize: "9px",
		fontWeight: 700,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
		color: "#FFFFFF",
		borderRadius: "3px",
		padding: "1px 6px",
	},
	reportRow: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink3,
		lineHeight: 1.45,
		marginTop: "3px",
	},
	reportLabel: {
		fontWeight: 600,
		color: inkColors.ink2,
		marginRight: "5px",
	},
});

export const PersonaReviewIssues: React.FC<{ runId: string; moduleId: string }> = ({ runId, moduleId }) => {
	const styles = useStyles();
	const [issues, setIssues] = useState<ReviewIssue[]>([]);
	const [verdict, setVerdict] = useState<string | null>(null);
	const [hasReview, setHasReview] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setIssues([]);
		setVerdict(null);
		setHasReview(false);
		getReviewIssues(runId, moduleId)
			.then((resp) => {
				if (cancelled) return;
				setHasReview(resp.has_review);
				setVerdict(resp.aggregated_verdict);
				setIssues(resp.issues ?? []);
			})
			.catch(() => {
				/* no verdict yet / not found — leave empty, no error surfaced */
			});
		return () => {
			cancelled = true;
		};
	}, [runId, moduleId]);

	if (!hasReview || issues.length === 0) return null;

	return (
		<div className={styles.card}>
			<div className={styles.cardHeader}>
				<span className={styles.cardTitle}>
					Section review issues
					{verdict ? ` · ${verdict.replace(/_/g, " ")}` : ""}
				</span>
			</div>
			<div className={styles.reportList}>
				{issues.map((iss, i) => (
					<div className={styles.report} key={`${iss.rubric_ref ?? iss.dimension ?? "issue"}-${i}`}>
						<div className={styles.reportHeader}>
							<span className={styles.reportTitle}>{iss.dimension || iss.rubric_ref || "Issue"}</span>
							{iss.severity ? (
								<span className={styles.badge} style={{ background: severityColor(iss.severity) }}>
									{iss.severity}
								</span>
							) : null}
						</div>
						{iss.description ? (
							<div className={styles.reportRow}>
								<span className={styles.reportLabel}>Issue</span>
								{iss.description}
							</div>
						) : null}
						{iss.suggestion ? (
							<div className={styles.reportRow}>
								<span className={styles.reportLabel}>Suggestion</span>
								{iss.suggestion}
							</div>
						) : null}
					</div>
				))}
			</div>
		</div>
	);
};

export default PersonaReviewIssues;
